import React, { useMemo } from 'react';
import { useChartColors } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import { useEmployeeEngagementDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store';
import { DataTable } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/DataTable';
import { EmptyStateContainer } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.styled';
import { DataTableColumn } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/FulfillmentDashboard.types';
import {
  INLINE_BAR_MAX_WIDTH_PERCENT,
  INLINE_BAR_MIN_WIDTH,
  INLINE_BAR_HEIGHT,
  INLINE_BAR_BORDER_RADIUS,
} from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.constants';

const RANK_MEDALS: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

function renderRank(value: string | number): React.ReactNode {
  const rank = typeof value === 'string' ? parseInt(value, 10) : value;
  return <span>{RANK_MEDALS[rank] || rank}</span>;
}

export const ShiftAcceptanceLeaderboard: React.FC = () => {
  const data = useEmployeeEngagementDashboardStore((s) => s.shiftAcceptanceLeaderboard);
  const palette = useChartColors();

  const maxShifts = useMemo(
    () => (data ? Math.max(...data.map((d) => d.shiftsAccepted)) : 1),
    [data],
  );

  const barColor = palette.chartColors[3];

  const renderShiftsAccepted = useMemo(
    () =>
      function ShiftsCell(value: string | number): React.ReactNode {
        const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
        const barWidth = Math.max(
          INLINE_BAR_MIN_WIDTH,
          (numValue / maxShifts) * INLINE_BAR_MAX_WIDTH_PERCENT,
        );
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <strong>{numValue}</strong>
            <span
              style={{
                display: 'inline-block',
                width: barWidth,
                height: INLINE_BAR_HEIGHT,
                backgroundColor: barColor,
                borderRadius: INLINE_BAR_BORDER_RADIUS,
              }}
            />
          </span>
        );
      },
    [maxShifts, barColor],
  );

  const columns: DataTableColumn[] = useMemo(
    () => [
      { key: 'rank', label: 'Rank', sortable: true, width: '60px', renderCell: renderRank },
      { key: 'teamMember', label: 'Team member', sortable: true },
      { key: 'position', label: 'Position', sortable: true },
      { key: 'type', label: 'Type', sortable: true },
      {
        key: 'shiftsAccepted',
        label: 'Shifts accepted',
        sortable: true,
        align: 'right' as const,
        renderCell: renderShiftsAccepted,
      },
    ],
    [renderShiftsAccepted],
  );

  const rows = useMemo(
    () =>
      data?.map((d) => ({
        rank: d.rank,
        teamMember: d.teamMember,
        position: d.position,
        type: d.type,
        shiftsAccepted: d.shiftsAccepted,
      })) ?? [],
    [data],
  );

  if (!data) {
    return <EmptyStateContainer>No data available</EmptyStateContainer>;
  }

  return <DataTable columns={columns} rows={rows} ariaLabel="Shift acceptance leaderboard table" />;
};
